/**
 * Pause overlay — in-match chrome. (§5.1, §9.1 TRAINING)
 *
 * A dumb view: game/practice.ts owns the actual freeze (stopping the
 * FixedStepDriver, ducking audio, releasing pointer lock); this only draws and
 * forwards clicks. Plain DOM, one style element, disposed clean — the same
 * discipline as hud.ts.
 *
 * Online matches never get this overlay: a server-authoritative match cannot
 * freeze for one client (§9.3 "Never pause the match for a disconnect", and the
 * same logic applies to a menu).
 *
 * ── Why it was rebuilt ─────────────────────────────────────────────────────
 * The previous overlay was a 300px box in 'Segoe UI' on a flat blue-grey
 * gradient with its own hardcoded #ffd94a. It predated the Footy Arena design
 * system and shared nothing with it, so the game's most-seen screen looked like
 * it came from a different product than the menu the player had just left.
 *
 * It now reads from the same tokens as footy-arena.css — the display face, the
 * gold, the ink ramp, the hairlines — with literal fallbacks in every `var()`
 * so it still renders correctly if it is ever mounted somewhere that stylesheet
 * is not loaded.
 *
 * ── The backdrop is the point ──────────────────────────────────────────────
 * A pause overlay's job is to say "the match is still there, it is just
 * stopped". So the live frame stays visible and is treated rather than covered:
 * blurred, desaturated, darkened toward the edges. A solid panel over the top
 * would throw away the one piece of art the screen already has.
 */

import { createNavOwner, type NavOwner } from './ui-nav.js';

export interface PauseCallbacks {
  onResume(): void;
  onSettings(): void;
  onQuitToMenu(): void;
}

export interface Overlay {
  dispose(): void;
}

interface RowSpec {
  key: string;
  label: string;
  hint: string;
  note: string;
  danger?: boolean;
}

const ROWS: readonly RowSpec[] = [
  { key: 'resume', label: 'Resume', hint: 'Esc', note: 'Back to the match' },
  { key: 'settings', label: 'Settings', hint: '', note: 'Camera, audio, HUD' },
  { key: 'quit', label: 'Quit to menu', hint: '', note: 'Abandon this match', danger: true },
];

export function mountPauseOverlay(container: HTMLElement, cb: PauseCallbacks): Overlay {
  const root = document.createElement('div');
  root.className = 'fgpause';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', 'Match paused');

  const rowsHtml = ROWS.map((r, i) => `
      <button type="button" class="fgpause-row${r.danger ? ' is-danger' : ''}" data-fg="${r.key}" style="--i:${i}">
        <span class="fgpause-row-index">${String(i + 1).padStart(2, '0')}</span>
        <span class="fgpause-row-text">
          <span class="fgpause-row-label">${r.label}</span>
          <span class="fgpause-row-note">${r.note}</span>
        </span>
        ${r.hint ? `<span class="fgpause-row-hint">${r.hint}</span>` : ''}
        <span class="fgpause-row-chev" aria-hidden="true"></span>
      </button>`).join('');

  root.innerHTML = `
    <div class="fgpause-veil"></div>
    <div class="fgpause-grain"></div>
    <div class="fgpause-panel">
      <p class="fgpause-kicker">Match paused</p>
      <h2 class="fgpause-title">Paused</h2>
      <div class="fgpause-list">${rowsHtml}</div>
      <p class="fgpause-foot">
        <span class="fgpause-badge">&uarr;</span><span class="fgpause-badge">&darr;</span> Move
        <span class="fgpause-badge">Enter</span> Select
      </p>
    </div>`;

  container.appendChild(root);
  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);

  const bind = (key: string, fn: () => void): HTMLButtonElement => {
    const b = root.querySelector(`[data-fg="${key}"]`) as HTMLButtonElement;
    b.addEventListener('click', fn);
    return b;
  };
  const buttons = [
    bind('resume', cb.onResume),
    bind('settings', cb.onSettings),
    bind('quit', cb.onQuitToMenu),
  ];

  /**
   * Keyboard AND gamepad, via the same owner every menu screen uses — a player
   * who paused with a controller has to be able to leave with one.
   *
   * Escape resumes. That does not fight practice.ts's own pause key: the match
   * loop only reads the menu button while `!paused`, and resume() clears any
   * latched one-shot (`takeOneShots(false)`) before the first live tick, so a
   * single Escape cannot resume and immediately re-pause.
   */
  const nav: NavOwner = createNavOwner({
    getNavElements: () => buttons,
    onBack: cb.onResume,
  });
  nav.setSelectedIndex(0);

  return {
    dispose(): void {
      nav.dispose();
      root.remove();
      style.remove();
    },
  };
}

const CSS = `
.fgpause {
  position: fixed; inset: 0; z-index: 30;
  display: grid; place-items: center;
  font-family: var(--fa-ui, 'Inter', system-ui, sans-serif);
  color: var(--fa-text, #f2f6fa);
  user-select: none;
  animation: fgpause-fade 220ms cubic-bezier(0.22,0.61,0.36,1) both;
}
@keyframes fgpause-fade { from { opacity: 0 } to { opacity: 1 } }

/* The live match, treated rather than hidden. backdrop-filter does the blur, so
   the frame underneath keeps animating (a goal replay behind a pause still
   plays) instead of being frozen to a flat screenshot.

   CRITICAL: the darkening lives in the 'background', NOT in the backdrop-filter's
   brightness(). backdrop-filter is an ENHANCEMENT here and nothing more — it is
   unsupported or silently dropped in a range of real conditions (software
   rasterisation, some Linux/GL stacks, older Safari, and every headless
   capture this repo's QA scripts run through). When it no-ops, a veil that
   relied on it for contrast leaves the whole overlay sitting on a fully lit
   pitch: caught exactly that way in the sweep, with the keyboard hints
   unreadable over grass. The gradient alone carries 0.74–0.93 alpha, so the
   overlay is legible with or without the blur. */
.fgpause-veil {
  position: absolute; inset: 0;
  backdrop-filter: blur(18px) saturate(0.75);
  -webkit-backdrop-filter: blur(18px) saturate(0.75);
  background:
    radial-gradient(58% 46% at 50% 40%, rgba(255,226,168,0.06) 0%, rgba(255,226,168,0) 70%),
    radial-gradient(122% 100% at 50% 45%, rgba(4,7,12,0.74) 32%, rgba(4,7,12,0.93) 100%);
}

/* Same procedural grain as the menu, so pausing does not change the surface
   texture of the frame. */
.fgpause-grain {
  position: absolute; inset: -50%;
  pointer-events: none; opacity: 0.34; mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.42'/%3E%3C/svg%3E");
  animation: fgpause-grain 900ms steps(1) infinite;
}
@keyframes fgpause-grain {
  0%{transform:translate3d(0,0,0)} 25%{transform:translate3d(1%,-1.8%,0)}
  50%{transform:translate3d(-2%,1.2%,0)} 75%{transform:translate3d(2%,-0.8%,0)}
}

.fgpause-panel {
  position: relative;
  width: min(520px, calc(100vw - 48px));
  padding: 4px 0 0;
  animation: fgpause-rise 320ms cubic-bezier(0.16,0.84,0.44,1) both;
}
@keyframes fgpause-rise { from { opacity:0; transform: translateY(14px) } to { opacity:1; transform:none } }

/* The gold tick — the menu's one accent gesture, reused so this screen is
   visibly part of the same product. */
.fgpause-kicker {
  position: relative; margin: 0 0 10px; padding-top: 18px;
  font-size: 12px; font-weight: 700; letter-spacing: 0.24em; text-transform: uppercase;
  color: var(--fa-accent, #e8c56a);
}
.fgpause-kicker::before {
  content: ''; position: absolute; top: 0; left: 0;
  width: 78px; height: 3px; background: var(--fa-accent, #e8c56a);
}
.fgpause-title {
  margin: 0 0 26px;
  font-family: var(--fa-display, 'Barlow Condensed', 'Arial Narrow', sans-serif);
  font-size: 64px; font-weight: 700; line-height: 0.92; letter-spacing: -0.005em;
  text-shadow: 0 6px 34px rgba(0,0,0,0.6);
}

.fgpause-list { display: flex; flex-direction: column; gap: 8px; }

/* Full-width rows, not centred buttons. A row can carry an index, a label and a
   one-line explanation of what it does, which a 130px pill cannot — "Quit to
   menu" is a destructive action and deserves to say so before it is clicked. */
.fgpause-row {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  border: 1px solid var(--fa-hairline, rgba(255,255,255,0.08));
  border-radius: var(--fa-r-md, 6px);
  background: rgba(10,16,24,0.55);
  backdrop-filter: blur(8px);
  color: inherit; font-family: inherit; text-align: left; cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease, transform 160ms ease;
  animation: fgpause-rise 340ms cubic-bezier(0.16,0.84,0.44,1) both;
  animation-delay: calc(80ms + var(--i, 0) * 55ms);
}
/* The accent bar that marks the active row. Scaled from the top edge rather
   than faded, so the eye tracks it moving down the list as you arrow through. */
.fgpause-row::before {
  content: ''; position: absolute; left: -1px; top: -1px; bottom: -1px;
  width: 3px; border-radius: var(--fa-r-md, 6px) 0 0 var(--fa-r-md, 6px);
  background: var(--fa-accent, #e8c56a);
  transform: scaleY(0); transform-origin: top;
  transition: transform 180ms cubic-bezier(0.16,0.84,0.44,1);
}
.fgpause-row:hover, .fgpause-row:focus-visible {
  outline: none;
  background: rgba(18,29,44,0.72);
  border-color: var(--fa-accent-line, rgba(232,197,106,0.5));
  transform: translateX(3px);
}
.fgpause-row:hover::before, .fgpause-row:focus-visible::before { transform: scaleY(1); }

.fgpause-row-index {
  font-family: var(--fa-display, 'Barlow Condensed', sans-serif);
  font-size: 14px; font-weight: 700; letter-spacing: 0.14em;
  color: var(--fa-steel-dim, #5d7086);
  transition: color 160ms ease;
}
.fgpause-row:hover .fgpause-row-index,
.fgpause-row:focus-visible .fgpause-row-index { color: var(--fa-accent, #e8c56a); }

.fgpause-row-text { display: block; }
.fgpause-row-label {
  display: block;
  font-family: var(--fa-display, 'Barlow Condensed', sans-serif);
  font-size: 25px; font-weight: 700; line-height: 1.05;
}
.fgpause-row-note {
  display: block; margin-top: 1px;
  font-size: 12px; font-weight: 500; letter-spacing: 0.04em;
  color: var(--fa-steel, #8fa6c0);
}
.fgpause-row-hint {
  padding: 3px 8px;
  border: 1px solid var(--fa-hairline-hi, rgba(255,255,255,0.16));
  border-radius: 4px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
  color: var(--fa-steel, #8fa6c0);
}
/* Chevron drawn in CSS rather than shipped as an icon — two borders and a
   rotation, no request, scales with the row. */
.fgpause-row-chev {
  width: 7px; height: 7px;
  border-top: 2px solid var(--fa-steel-dim, #5d7086);
  border-right: 2px solid var(--fa-steel-dim, #5d7086);
  transform: rotate(45deg);
  transition: border-color 160ms ease, transform 160ms ease;
}
.fgpause-row:hover .fgpause-row-chev,
.fgpause-row:focus-visible .fgpause-row-chev {
  border-color: var(--fa-accent, #e8c56a);
  transform: rotate(45deg) translate(2px, -2px);
}

/* Quit is destructive: it goes red on approach, not by default. A permanently
   red row in a list of three trains the eye to ignore it. */
.fgpause-row.is-danger:hover, .fgpause-row.is-danger:focus-visible {
  border-color: rgba(233,116,116,0.55);
  background: rgba(44,18,20,0.6);
}
.fgpause-row.is-danger::before { background: #e97474; }
.fgpause-row.is-danger:hover .fgpause-row-index,
.fgpause-row.is-danger:focus-visible .fgpause-row-index,
.fgpause-row.is-danger:hover .fgpause-row-chev,
.fgpause-row.is-danger:focus-visible .fgpause-row-chev { color: #e97474; border-color: #e97474; }

.fgpause-foot {
  display: flex; align-items: center; gap: 8px;
  margin: 22px 0 0;
  font-size: 12px; color: var(--fa-steel-dim, #5d7086);
  animation: fgpause-rise 340ms cubic-bezier(0.16,0.84,0.44,1) 280ms both;
}
.fgpause-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 20px; padding: 0 6px;
  border: 1px solid var(--fa-hairline-hi, rgba(255,255,255,0.16));
  border-radius: 4px;
  font-size: 11px; font-weight: 700; color: var(--fa-steel, #8fa6c0);
}
.fgpause-foot .fgpause-badge + .fgpause-badge { margin-left: -4px; }

@media (max-height: 620px) {
  .fgpause-title { font-size: 42px; margin-bottom: 16px; }
  .fgpause-row { padding: 10px 16px; }
  .fgpause-row-label { font-size: 21px; }
  .fgpause-row-note { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .fgpause, .fgpause-panel, .fgpause-row, .fgpause-foot, .fgpause-grain {
    animation-duration: 0.001ms !important;
    animation-delay: 0ms !important;
    animation-iteration-count: 1 !important;
  }
}
`;
